import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles } from "lucide-react";

export const BrandMark = ({ className = "" }) => (
  <Link
    to="/"
    data-testid="brand-mark"
    className={`group inline-flex items-center gap-2 font-display font-extrabold text-xl tracking-tight ${className}`}
  >
    <span className="relative grid place-items-center h-8 w-8 rounded-md bg-primary text-primary-foreground overflow-hidden">
      <span className="font-mono text-sm relative z-10">F</span>
      <span className="absolute inset-0 bg-gradient-to-br from-secondary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    </span>
    FlexPay
  </Link>
);

export default function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-10 relative">
        <div className="absolute inset-0 dotted-pattern opacity-40 pointer-events-none" />
        <div className="relative">
          <BrandMark />
        </div>
        <div className="relative flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto animate-fade-up">
            <span className="overline">Partner Portal</span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-2">
              {title}
            </h1>
            {subtitle && (
              <p className="text-muted-foreground mt-3 leading-relaxed">{subtitle}</p>
            )}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-8 text-sm text-muted-foreground">{footer}</div>}
          </div>
        </div>
        <div className="relative text-xs text-muted-foreground flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
          Bank-grade encryption. PCI DSS aligned. RBI guidelines compliant.
        </div>
      </div>

      {/* Right: hero — blue gradient composition */}
      <div className="hidden lg:block relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="absolute -top-32 -right-20 h-[28rem] w-[28rem] rounded-full bg-secondary/40 blur-3xl animate-float-slow" />
        <div className="absolute -bottom-40 -left-10 h-[28rem] w-[28rem] rounded-full bg-white/10 blur-3xl animate-float-slow" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold">
            <span className="relative inline-flex h-2 w-2 rounded-full bg-secondary pulse-dot text-secondary" />
            Live · processing
          </div>

          {/* Floating glass cards */}
          <div className="relative flex-1 flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-[4/5]">
              <GlassCard
                title="Credit Card Bill"
                amount="₹ 42,800.00"
                status="Paid"
                time="2s ago"
                className="absolute top-0 left-0 rotate-[-4deg] animate-float-slow"
                delay="0s"
              />
              <GlassCard
                title="Payout · UPI"
                amount="₹ 1,20,000.00"
                status="Settled"
                time="just now"
                className="absolute top-24 right-0 rotate-[3deg] animate-float-slow"
                delay="1.4s"
                accent
              />
              <GlassCard
                title="Electricity · BESCOM"
                amount="₹ 3,450.00"
                status="Processing"
                time="live"
                className="absolute bottom-0 left-6 rotate-[-2deg] animate-float-slow"
                delay="2.8s"
                pending
              />
            </div>
          </div>

          <div className="max-w-md">
            <span className="overline text-primary-foreground/70">Built for payment partners</span>
            <h2 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tighter mt-3">
              Process BBPS &amp; payouts on behalf of your consumers — at scale.
            </h2>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { k: "10+", v: "BBPS categories" },
                { k: "24x7", v: "Payout rails" },
                { k: "<30s", v: "Avg. settlement" },
              ].map((s) => (
                <div key={s.v} className="border border-primary-foreground/20 p-3 rounded-md backdrop-blur-sm bg-white/5 hover:bg-white/10 transition-colors">
                  <div className="font-mono text-2xl">{s.k}</div>
                  <div className="text-xs text-primary-foreground/70 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GlassCard({ title, amount, status, time, className = "", delay = "0s", accent, pending }) {
  const chip = pending
    ? "bg-warning/20 text-warning border-warning/40"
    : accent
    ? "bg-secondary text-secondary-foreground border-secondary"
    : "bg-success/20 text-success border-success/40";
  return (
    <div
      style={{ animationDelay: delay }}
      className={`w-64 rounded-xl border border-white/20 bg-white/10 backdrop-blur-md p-4 shadow-2xl ${className}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-primary-foreground/60 font-bold">
          {title}
        </span>
        <Sparkles className="h-3.5 w-3.5 text-primary-foreground/60" strokeWidth={1.5} />
      </div>
      <div className="mt-3 font-mono text-2xl font-semibold">{amount}</div>
      <div className="mt-4 flex items-center justify-between">
        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${chip}`}>
          {status}
        </span>
        <span className="text-[11px] font-mono text-primary-foreground/60">{time}</span>
      </div>
    </div>
  );
}
